import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Volume1,
  Heart, ChevronDown, Shuffle, Repeat, Repeat1, X,
  ListMusic, Clock, Music, Lock
} from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';

interface PlayerExpandedProps {
  isOpen: boolean;
  onClose: () => void;
  currentTime: number;
  volume: number;
  onVolumeChange: (vol: number) => void;
  onSeek: (time: number) => void;
  shuffle: boolean;
  onShuffleToggle: () => void;
  repeatMode: 'off' | 'all' | 'one';
  onRepeatCycle: () => void;
}

export const PlayerExpanded: React.FC<PlayerExpandedProps> = ({
  isOpen, onClose, currentTime, volume, onVolumeChange,
  onSeek, shuffle, onShuffleToggle, repeatMode, onRepeatCycle
}) => {
  const { state, dispatch } = usePlayer();
  const [showQueue, setShowQueue] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  const isPreviewMode = state.isPreviewMode && !state.hasFullAccess && !state.currentTrack?.isPurchased;
  const duration = state.currentTrack?.duration || 0;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !state.currentTrack) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    let newTime = percent * duration;
    if (isPreviewMode && newTime > 30) {
      newTime = 30;
      toast.info('Subscribe to listen to the full track');
    }
    onSeek(newTime);
  };

  const VolumeIcon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!state.currentTrack) return null;

  const coverArt = state.currentTrack.cover_art_url || state.currentTrack.coverArt ||
    'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=600&q=80';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 z-50 bg-stone-950 overflow-hidden"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${coverArt})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(80px) brightness(0.3)',
              transform: 'scale(1.2)',
            }}
          />
          <div className="absolute inset-0 bg-black/40" />

          <div className="relative h-full flex flex-col">
            <div className="flex items-center justify-between px-6 py-4">
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ChevronDown className="w-6 h-6 text-white" />
              </button>
              <div className="text-center">
                <p className="text-white/60 text-xs uppercase tracking-wider font-medium">Now Playing</p>
              </div>
              <button
                onClick={() => setShowQueue(!showQueue)}
                className={`p-2 rounded-full transition-colors ${showQueue ? 'bg-white/20 text-white' : 'hover:bg-white/10 text-white/60'}`}
              >
                <ListMusic className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 overflow-hidden">
              <AnimatePresence mode="wait">
                {showQueue ? (
                  <motion.div
                    key="queue"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    className="w-full max-w-lg"
                  >
                    <QueuePanel />
                  </motion.div>
                ) : (
                  <motion.div
                    key="artwork"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="w-full max-w-md"
                  >
                    <motion.img
                      src={coverArt}
                      alt={state.currentTrack.title}
                      className="w-full aspect-square object-cover rounded-2xl shadow-2xl"
                      layoutId="player-cover"
                      style={{ boxShadow: '0 25px 80px rgba(0,0,0,0.5)' }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="px-6 pb-8 max-w-lg mx-auto w-full space-y-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold text-white truncate">{state.currentTrack.title}</h2>
                  <p className="text-white/60 text-sm truncate">
                    {state.currentTrack.artist_name || state.currentTrack.artist || 'Unknown Artist'}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {isPreviewMode && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-white/10 rounded-full text-xs text-white/70">
                      <Lock className="w-3 h-3" />
                      Preview
                    </span>
                  )}
                  <button className="p-2.5 hover:bg-white/10 rounded-full transition-colors group">
                    <Heart className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div
                  ref={progressRef}
                  onClick={handleProgressClick}
                  className="h-1.5 bg-white/20 rounded-full cursor-pointer group relative overflow-hidden hover:h-2 transition-all"
                >
                  <div
                    className="absolute top-0 left-0 h-full bg-white rounded-full transition-[width]"
                    style={{ width: `${progressPercent}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {isPreviewMode && duration > 0 && (
                    <div
                      className="absolute top-0 h-full bg-red-500/20 pointer-events-none"
                      style={{ left: `${(30 / duration) * 100}%`, right: 0 }}
                    />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/40 tabular-nums">{formatTime(currentTime)}</span>
                  <span className="text-xs text-white/40 tabular-nums">{formatTime(duration)}</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6">
                <button
                  onClick={onShuffleToggle}
                  className={`p-2 rounded-full transition-colors ${shuffle ? 'text-royal-gold' : 'text-white/40 hover:text-white/70'}`}
                >
                  <Shuffle className="w-5 h-5" />
                </button>
                <button
                  onClick={() => dispatch({ type: 'PREVIOUS_TRACK' })}
                  className="p-3 hover:bg-white/10 rounded-full transition-colors text-white"
                >
                  <SkipBack className="w-6 h-6" fill="currentColor" />
                </button>
                <button
                  onClick={() => dispatch({ type: 'TOGGLE_PLAY' })}
                  className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-xl"
                >
                  {state.isPlaying ? (
                    <Pause className="w-7 h-7 text-stone-900" fill="currentColor" />
                  ) : (
                    <Play className="w-7 h-7 text-stone-900 ml-1" fill="currentColor" />
                  )}
                </button>
                <button
                  onClick={() => dispatch({ type: 'NEXT_TRACK' })}
                  className="p-3 hover:bg-white/10 rounded-full transition-colors text-white"
                >
                  <SkipForward className="w-6 h-6" fill="currentColor" />
                </button>
                <button
                  onClick={onRepeatCycle}
                  className={`p-2 rounded-full transition-colors ${repeatMode !== 'off' ? 'text-royal-gold' : 'text-white/40 hover:text-white/70'}`}
                >
                  {repeatMode === 'one' ? <Repeat1 className="w-5 h-5" /> : <Repeat className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => onVolumeChange(volume === 0 ? 0.7 : 0)}
                  className="p-1 text-white/40 hover:text-white/70 transition-colors"
                >
                  <VolumeIcon className="w-4 h-4" />
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={e => onVolumeChange(parseFloat(e.target.value))}
                  className="w-28 h-1 bg-white/20 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                    [&::-webkit-slider-thumb]:cursor-pointer"
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const QueuePanel: React.FC = () => {
  const { state, dispatch } = usePlayer();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Queue</h3>
        {state.queue.length > 0 && (
          <button
            onClick={() => dispatch({ type: 'CLEAR_QUEUE' })}
            className="text-xs text-white/50 hover:text-white/80 transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {state.currentTrack && (
        <div className="space-y-2">
          <p className="text-xs text-white/40 uppercase tracking-wider font-medium">Now Playing</p>
          <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
            <img
              src={state.currentTrack.cover_art_url || state.currentTrack.coverArt || ''}
              alt=""
              className="w-10 h-10 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{state.currentTrack.title}</p>
              <p className="text-xs text-white/50 truncate">{state.currentTrack.artist_name || state.currentTrack.artist}</p>
            </div>
            <div className="w-3 h-3 rounded-full bg-royal-gold animate-pulse" />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs text-white/40 uppercase tracking-wider font-medium">
          Up Next {state.queue.length > 0 && `(${state.queue.length})`}
        </p>
        {state.queue.length === 0 ? (
          <div className="py-8 text-center">
            <Music className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-sm text-white/30">Queue is empty</p>
            <p className="text-xs text-white/20 mt-1">Add songs to your queue</p>
          </div>
        ) : (
          <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
            {state.queue.map((track, i) => (
              <div
                key={`${track.id}-${i}`}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="text-xs text-white/30 w-5 text-center">{i + 1}</span>
                <img
                  src={track.cover_art_url || track.coverArt || ''}
                  alt=""
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/80 truncate">{track.title}</p>
                  <p className="text-xs text-white/40 truncate">{track.artist_name || track.artist}</p>
                </div>
                <button
                  onClick={() => dispatch({ type: 'REMOVE_FROM_QUEUE', payload: i })}
                  className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-full transition-all"
                >
                  <X className="w-3.5 h-3.5 text-white/50" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {state.history.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-white/10">
          <p className="text-xs text-white/40 uppercase tracking-wider font-medium">
            <Clock className="w-3 h-3 inline mr-1" />
            Recently Played
          </p>
          <div className="space-y-1 max-h-[20vh] overflow-y-auto pr-1">
            {[...state.history].reverse().slice(0, 5).map((track, i) => (
              <div
                key={`hist-${track.id}-${i}`}
                onClick={() => dispatch({ type: 'SET_TRACK', payload: track })}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition-colors cursor-pointer group"
              >
                <img
                  src={track.cover_art_url || track.coverArt || ''}
                  alt=""
                  className="w-9 h-9 rounded-lg object-cover opacity-60"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/50 truncate">{track.title}</p>
                  <p className="text-xs text-white/30 truncate">{track.artist_name || track.artist}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
